// =============================================================================
// Activity Page — Change log & audit trail (R32-R34).
//   R32: Full who/what/when history
//   R33: Recent-change highlight on migration dates
//   R34: Data retention (could-have, shown as info)
// =============================================================================
import { useState } from 'react'
import { api } from '../lib/api'
import { useAsync } from '../hooks/useAsync'
import { Card, Button, Skeleton, ErrorState, SectionTitle } from '../components/ui'
import { fmtDateTime, displayValue } from '../lib/format'
import type { ChangeLogEntry } from '../lib/types'

export function ActivityPage() {
  const [hours, setHours] = useState(168) // default 7 days
  const { data, loading, error, reload } = useAsync<ChangeLogEntry[]>(
    () => api.changeLog({ hours, limit: 500 }), [hours])

  return (
    <div className="space-y-4">
      <SectionTitle sub="Who changed what and when across the programme">
        Activity Log
      </SectionTitle>

      {/* Time range selector */}
      <div className="flex items-center gap-2">
        <span className="text-[12px] text-muted">Show changes from last:</span>
        {[
          { h: 24, label: '24h' }, { h: 48, label: '48h' }, { h: 168, label: '7 days' },
          { h: 720, label: '30 days' }, { h: 2160, label: '90 days' },
        ].map((opt) => (
          <button key={opt.h} onClick={() => setHours(opt.h)}
            className={`rounded-full border px-2.5 py-1 text-[12px] font-medium ${hours === opt.h ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line text-muted hover:border-slate-300'}`}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <Card className="p-4">
        {loading && !data ? <Skeleton className="h-60" /> : error && !data ? <ErrorState message={error} onRetry={reload} /> : (
          <>
            <p className="mb-4 text-[12px] text-muted">{data?.length || 0} changes in this period</p>
            {!data || data.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted">No activity in this period.</p>
            ) : (
              <ol className="relative space-y-3 border-l border-line pl-5">
                {data.map((e) => {
                  const isMigDate = e.field === 'migration_date'
                  return (
                    <li key={e.id} className={`relative ${isMigDate ? 'rounded bg-amber-50 p-2 -ml-2' : ''}`}>
                      <span className={`absolute -left-[23px] top-1 h-2.5 w-2.5 rounded-full border-2 border-surface ${isMigDate ? 'bg-rag-amber' : 'bg-brand-600'}`} />
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[12px] font-medium text-ink">
                          <span className="font-semibold text-brand-700">{e.site_id}</span> · {e.field.replace(/_/g, ' ')}
                        </span>
                        <span className="flex-shrink-0 text-[11px] text-muted">{fmtDateTime(e.changed_at)}</span>
                      </div>
                      <p className="mt-0.5 text-[11px] text-muted">
                        <span className="line-through opacity-70">{displayValue(e.old_value)}</span> → <span className="font-medium text-ink">{displayValue(e.new_value)}</span>
                      </p>
                      {e.changed_by_name && <p className="mt-0.5 text-[10px] text-slate-400">by {e.changed_by_name}{e.changed_by_role ? ` · ${e.changed_by_role}` : ''}</p>}
                    </li>
                  )
                })}
              </ol>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
